/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useMemo } from 'react';

import { ISSUE_NAVIGATOR_URL } from '../../../../common/constants/issue-navigator';
import { useAppContext } from '../../../../common/ui/app-context';
import {
	INTENT_DETECTION,
	IntentMode,
	PatternType,
} from '../../../../common/utils/intent-detection';
import { getWorkItemPageUrl } from '../../../../common/utils/quick-find/jira-work-item-navigation';
import { useSelectedFilters } from '../../../../controllers/filters/selected';

import { isSamePathname } from './utils';

export type AllAppsUrlResult = {
	allAppsUrl: string;
	isAllAppsPage: boolean;
	defaultMode: IntentMode;
};

export type WorkItemsUrlResult = {
	jiraWorkItemsUrl: string;
	isJiraWorkItemsPage: boolean;
};

/**
 * Build the All-apps page URL for the current query, detect whether the user
 * is already on that page, and pick the default intent-router mode (FPS when
 * on the All-apps page, GNIN otherwise — with the cached `INTENT_DETECTION`
 * result for the current query taking precedence).
 */
export const useAllAppsUrl = ({
	query,
	minQueryLength,
}: {
	query: string;
	minQueryLength: number;
}): AllAppsUrlResult => {
	const { generateSearchUrl, queryParams } = useAppContext();

	const allAppsUrl = useMemo(
		() =>
			generateSearchUrl({
				...queryParams,
				text: query,
			}),
		[generateSearchUrl, queryParams, query],
	);

	const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
	const isAllAppsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, allAppsUrl) : false),
		[currentUrl, allAppsUrl],
	);

	let defaultMode = isAllAppsPage ? IntentMode.FPS : IntentMode.GNIN;

	// Hydrate the default mode from the intent detection result if available.
	const intentDetectionResult = INTENT_DETECTION.value();
	if (intentDetectionResult) {
		const { mode, query: intentDetectionQuery } = intentDetectionResult;
		// If the intent detection result is for the current page, use it as the default mode.
		if (query.length >= minQueryLength && query === intentDetectionQuery) {
			defaultMode = mode;
		}
	}

	return {
		allAppsUrl,
		isAllAppsPage,
		defaultMode,
	};
};

/**
 * Build the Jira-work-items page URL (the destination of the GNIN link) for
 * the current query, and detect whether the user is already on that page.
 */
export const useWorkItemsUrl = ({
	query,
	showMatchingWorkItem,
	isLoading,
	patternType,
}: {
	query: string;
	showMatchingWorkItem?: boolean;
	isLoading?: boolean;
	patternType?: string;
}): WorkItemsUrlResult => {
	const { generateSearchUrl, queryParams } = useAppContext();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const isQueryJql = patternType === PatternType.Jql;

	const jiraWorkItemsUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query,
				jiraFilters,
				generateSearchUrl,
				queryParams,
				// use smart url to check if the query is an issue key while loading
				issueKeyLookup: isLoading || !showMatchingWorkItem,
				isQueryJql,
			});
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [
		generateSearchUrl,
		jiraFilters,
		queryParams,
		query,
		showMatchingWorkItem,
		isLoading,
		isQueryJql,
	]);

	const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
	const isJiraWorkItemsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, jiraWorkItemsUrl) : false),
		[currentUrl, jiraWorkItemsUrl],
	);

	return {
		jiraWorkItemsUrl,
		isJiraWorkItemsPage,
	};
};
